import { useEffect, useRef } from 'react';

// Horizontal travel that commits the gesture.
const TRIGGER_DISTANCE_PX = 70;
// The swipe has to be clearly sideways, not a diagonal scroll.
const HORIZONTAL_DOMINANCE = 1.5;
// Once the finger drifts this far vertically, the user is scrolling — give up.
const VERTICAL_ABANDON_PX = 35;

// A right swipe that starts inside a container the user can still scroll left
// belongs to that container (carousels, wide tables), not to us.
function startsInsideScrollableRegion(target) {
  let node = target instanceof Element ? target : null;

  while (node && node !== document.body) {
    if (node.scrollWidth > node.clientWidth + 1 && node.scrollLeft > 0) {
      const overflowX = window.getComputedStyle(node).overflowX;
      if (overflowX === 'auto' || overflowX === 'scroll') return true;
    }
    node = node.parentElement;
  }

  return false;
}

// Fires `onSwipe` when the user swipes left-to-right anywhere on the screen.
export default function useRightSwipe({ enabled, onSwipe }) {
  const onSwipeRef = useRef(onSwipe);
  onSwipeRef.current = onSwipe;

  useEffect(() => {
    if (!enabled) return undefined;

    let tracking = false;
    let startX = 0;
    let startY = 0;

    const handleTouchStart = (event) => {
      if (event.touches.length !== 1) {
        tracking = false;
        return;
      }
      const touch = event.touches[0];
      tracking = !startsInsideScrollableRegion(event.target);
      startX = touch.clientX;
      startY = touch.clientY;
    };

    const handleTouchMove = (event) => {
      if (!tracking) return;
      if (event.touches.length !== 1) {
        tracking = false;
        return;
      }

      const touch = event.touches[0];
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;

      if (Math.abs(dy) > VERTICAL_ABANDON_PX) {
        tracking = false;
        return;
      }

      if (dx >= TRIGGER_DISTANCE_PX && dx > Math.abs(dy) * HORIZONTAL_DOMINANCE) {
        tracking = false;
        onSwipeRef.current?.();
      }
    };

    const stopTracking = () => {
      tracking = false;
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', stopTracking, { passive: true });
    document.addEventListener('touchcancel', stopTracking, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', stopTracking);
      document.removeEventListener('touchcancel', stopTracking);
    };
  }, [enabled]);
}
